'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import AdsSafeStorage from 'AdsUI/lib/AdsSafeStorage';
import { Set as ImmutableSet } from 'immutable';
import { fetchDrafts, deleteDraft } from 'AdsUI/actions/DraftsActions';
import { DRAFTS_FILTERS } from 'AdsUI/lib/StorageKeys';
import { getConnectedUsers, getAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import DashboardNavigation from 'AdsUI/components/DashboardNavigation';
import DashboardNavigationItems from 'AdsUI/lib/DashboardNavigationItems';
import UIToolBar from 'UIComponents/nav/UIToolBar';
import UIToolBarGroup from 'UIComponents/nav/UIToolBarGroup';
import AdAccountSelect from 'AdsUI/components/AdAccountSelect';
import withQueryParams from 'AdsUI/lib/withQueryParams';
import UISearchInput from 'UIComponents/input/UISearchInput';
import I18n from 'I18n';
import CampaignsSidebar from 'AdsUI/components/CampaignsSidebar';
import { getDraftsData, getDraftsRequestStatus, getHasAnyDrafts } from 'AdsUI/selectors/DraftsSelectors';
import DraftsTable from 'AdsUI/components/campaigns/DraftsTable';
import { SortDirections, AdCreationTypes } from 'AdsUI/lib/Constants';
import { DraftSortColumns } from 'AdsUI/records/ad-management/DraftRecord';
import DraftDeleteModal from 'AdsUI/components/campaigns/DraftDeleteModal';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { AdAccountErrorStates } from 'AdsUI/records/ad-accounts/AdAccountRecord';
import { getUserCanCreateMarketableContacts, hasUsersAndTeams } from 'AdsUI/selectors/AuthSelectors';
import { getAdAccountsByUserAndTeams } from 'AdsUI/selectors/GlobalFilters';
import GlobalUsersAndTeamsFilterContainer from 'AdsUI/components/GlobalUsersAndTeamsFilterContainer';
import { getTeamsInfoRequestStatus } from 'AdsUI/selectors/UsersAndTeamsSelectors'; // This error is ignorable because the draft row will be disabled.

const IGNORABLE_ACCOUNT_ERRORS = [AdAccountErrorStates.LEAD_AD_PERMISSIONS_NEEDED, AdAccountErrorStates.CURRENT_USER_HAS_NOT_CONNECTED];
const DRAFT_TYPE_TO_ROUTE = {
  [AdCreationTypes.LEAD_AD]: 'lead-generation',
  [AdCreationTypes.TRAFFIC]: 'website-traffic',
  [AdCreationTypes.SEARCH]: 'search',
  [AdCreationTypes.POST_ENGAGEMENT]: 'post-engagement',
  [AdCreationTypes.SEQUENCE]: 'sequence'
};

const DraftsContainer = ({
  adAccounts,
  adAccountsByUserAndTeams,
  connectedUsers,
  history,
  query,
  draftsData,
  draftsRS,
  hasAnyDrafts,
  canCreateMarketableContacts,
  hasAccessToUsersAndTeams,
  setQueryParam,
  dispatch,
  teamsInfoRequestStatus
}) => {
  const accountsToUse = hasAccessToUsersAndTeams ? adAccountsByUserAndTeams : adAccounts;
  const [adAccountFilter, setAdAccountFilter] = useState(ImmutableSet([]));
  const searchQuery = query.get('search') || '';
  const onSearchChange = useCallback(e => {
    setQueryParam('search', e.target.value, false, ['page']);
  }, [setQueryParam]);
  const [{
    sortKey,
    direction
  }, setSort] = useState({
    sortKey: DraftSortColumns.NAME,
    direction: SortDirections.DESCENDING
  });
  const [draftToDelete, setDraftToDelete] = useState(null);
  useEffect(() => {
    const dehydratedFilters = JSON.parse(AdsSafeStorage.getItem(DRAFTS_FILTERS) || '{}');
    const hydratedAdAccounts = dehydratedFilters.adAccounts || [];

    const _adAccountFilter = accountsToUse.filter(account => {
      const errorState = account.getErrorState(connectedUsers);
      const hydratedAdAccount = hydratedAdAccounts.find(adAccount => adAccount.accountId === account.accountId && adAccount.network === account.network);
      return (errorState === AdAccountErrorStates.NONE || IGNORABLE_ACCOUNT_ERRORS.includes(errorState)) && hydratedAdAccount !== undefined;
    }).toSet();

    setAdAccountFilter(_adAccountFilter);
  }, [accountsToUse, connectedUsers]);
  useEffect(() => {
    const {
      fetched: fetchedTeamsinfo
    } = teamsInfoRequestStatus; // fetchDrafts depends on teamInfo response (not in early requester)

    if (hasAccessToUsersAndTeams && !fetchedTeamsinfo) {
      return;
    }

    if (!draftsRS.fetched && !draftsRS.fetching) {
      dispatch(fetchDrafts());
    }
  }, [hasAccessToUsersAndTeams, draftsRS, teamsInfoRequestStatus, dispatch]);
  const draftList = useMemo(() => {
    const sortedDrafts = draftsData.toList().filter(draft => !!adAccountFilter.find(account => account.accountId === draft.adAccountId && account.network === draft.adNetwork) && (!searchQuery || draft.name.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1)).sortBy(draft => {
      if (sortKey === DraftSortColumns.AD_ACCOUNT) {
        return accountsToUse.find(account => account.network === draft.adNetwork && account.accountId === draft.adAccountId).getShortName();
      }

      if (sortKey === DraftSortColumns.TYPE) {
        return I18n.text(`campaigns.drafts.types.${draft.displayType}`);
      }

      return draft[sortKey];
    });

    if (direction === SortDirections.ASCENDING) {
      return sortedDrafts.reverse();
    }

    return sortedDrafts;
  }, [draftsData, adAccountFilter, searchQuery, sortKey, direction, accountsToUse]);
  const onClick = useCallback(draft => {
    usageTracker.track('adCreationAction', {
      action: 'Opened draft',
      type: draft.displayType,
      network: draft.adNetwork
    });
    history.push(`/campaigns/${draft.adNetwork.toLowerCase()}/${DRAFT_TYPE_TO_ROUTE[draft.displayType]}/drafts/${draft.id}`);
  }, [history]);
  const onClickDelete = useCallback(draft => setDraftToDelete(draft), []);
  const onDelete = useCallback(() => {
    usageTracker.track('adCreationAction', {
      action: 'Deleted draft',
      type: draftToDelete.type,
      network: draftToDelete.adNetwork
    });
    dispatch(deleteDraft(draftToDelete));
    setDraftToDelete(null);
  }, [dispatch, draftToDelete]);
  const onCancelDelete = useCallback(() => setDraftToDelete(null), []);

  const handleFilterChange = newFilters => {
    setAdAccountFilter(newFilters);
    const dehydratedFilters = JSON.parse(AdsSafeStorage.getItem(DRAFTS_FILTERS) || '{}');
    dehydratedFilters.adAccounts = newFilters.toJS();
    AdsSafeStorage.setItem(DRAFTS_FILTERS, JSON.stringify(dehydratedFilters));
  };

  return /*#__PURE__*/_jsxs(UIDashboardPage, {
    headerComponent: /*#__PURE__*/_jsx(DashboardNavigation, {
      activeItem: DashboardNavigationItems.PAID_CAMPAIGNS
    }),
    sidebarComponent: /*#__PURE__*/_jsx(CampaignsSidebar, {}),
    pageLayout: "full-width",
    children: [/*#__PURE__*/_jsxs(UIToolBar, {
      use: "filter-bar",
      className: "m-top-0",
      children: [/*#__PURE__*/_jsxs(UIToolBarGroup, {
        children: [hasAccessToUsersAndTeams && /*#__PURE__*/_jsx(GlobalUsersAndTeamsFilterContainer, {}), /*#__PURE__*/_jsx(AdAccountSelect, {
          adAccounts: accountsToUse,
          accountFilter: adAccountFilter,
          connectedUsers: connectedUsers,
          onFilterChange: handleFilterChange,
          ignorableAccountErrors: IGNORABLE_ACCOUNT_ERRORS,
          disablePaymentMissingAccount: true
        })]
      }), /*#__PURE__*/_jsx(UIToolBarGroup, {
        children: /*#__PURE__*/_jsx(UISearchInput, {
          use: "on-dark",
          value: searchQuery,
          placeholder: I18n.text('campaigns.campaignTable.filterCampaigns'),
          onChange: onSearchChange,
          className: "display-inline-block inline-search-input-header"
        })
      })]
    }), /*#__PURE__*/_jsx(DraftsTable, {
      drafts: draftList,
      adAccounts: adAccounts,
      requestStatus: draftsRS,
      sortKey: sortKey,
      sortDirection: direction,
      onSortChange: setSort,
      onDelete: onClickDelete,
      onClick: onClick,
      hasAnyDrafts: hasAnyDrafts,
      canCreateMarketableContacts: canCreateMarketableContacts
    }), draftToDelete && /*#__PURE__*/_jsx(DraftDeleteModal, {
      draftName: draftToDelete.name,
      onConfirm: onDelete,
      onReject: onCancelDelete
    })]
  });
};

export default withQueryParams(connect(state => ({
  adAccounts: getAdAccounts(state),
  adAccountsByUserAndTeams: getAdAccountsByUserAndTeams(state),
  connectedUsers: getConnectedUsers(state),
  draftsData: getDraftsData(state),
  draftsRS: getDraftsRequestStatus(state),
  hasAnyDrafts: getHasAnyDrafts(state),
  canCreateMarketableContacts: getUserCanCreateMarketableContacts(state),
  hasAccessToUsersAndTeams: hasUsersAndTeams(state),
  teamsInfoRequestStatus: getTeamsInfoRequestStatus(state)
}))(DraftsContainer));